import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanDeactivate, RedirectCommand, Router, RouterStateSnapshot } from "@angular/router";
import { MaintenanceBreakComponent } from "../pages/maintenance-break/maintenance-break.component";

/**
 * Guard that controls whether the user can navigate away from
 * the {@link MaintenanceBreakComponent}.
 *
 * This guard ensures that:
 * - The user can leave only if the navigation was triggered by
 *   a skip maintenance action (`bySkipMaintenanceAction` flag set in `NavigationExtras.info`).
 * - Otherwise, the guard issues a redirect back to the same URL,
 *   effectively blocking unintended navigation away from the maintenance page.
 *
 * @example
 * ```ts
 * {
 *   path: 'maintenance-break',
 *   component: MaintenanceBreakComponent,
 *   canDeactivate: [CanDeactivateMaintenanceBreak]
 * }
 * ```
 */
@Injectable({ providedIn: "root" })
export class CanDeactivateMaintenanceBreak implements CanDeactivate<MaintenanceBreakComponent> {
  /** Router instance used to inspect navigation extras and create redirect commands. */
  private readonly router = inject(Router);

  /**
   * Determines if navigation away from the maintenance break page is allowed.
   *
   * @param _component - The instance of `MaintenanceBreakComponent` (unused here).
   * @param _currentRoute - The currently active route snapshot (unused here).
   * @param currentState - The router state snapshot representing the current URL.
   * @returns
   * - `true` if the navigation was triggered by a skip action (`bySkipMaintenanceAction` flag present).
   * - A `RedirectCommand` that navigates back to the same URL (without changing browser history)
   *   if the skip action flag is missing.
   */
  canDeactivate(_component: MaintenanceBreakComponent, _currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot) {
    const info = this.router.getCurrentNavigation()?.extras?.info as Record<string, unknown> | null;
    return (
      !!info?.["bySkipMaintenanceAction"] ||
      new RedirectCommand(this.router.parseUrl(currentState.url), { onSameUrlNavigation: "ignore", skipLocationChange: true })
    );
  }
}
